// Light formatting for member-written posts and replies, stored as plain text.
//
// Supported, and nothing else:
//   **bold**   _italic_   "- " bullets   "1. " numbered lists
//
// Why a Markdown subset in a textarea rather than a rich-text editor: the DB
// keeps plain text (no migration, every existing post renders as before, the
// char_length caps still mean what they say), there is no HTML to sanitise,
// and a textarea behaves predictably in the iOS WKWebView where contenteditable
// editors do not.
//
// ⚠️ Deliberately NO link syntax — see src/lib/linkify.js. Bare URLs still
// become links, including inside bold/italic. @handles become mention nodes
// (src/lib/mentions.js) in any text that isn't part of a URL.
//
// Nothing here produces HTML. parseFormatted returns a plain node tree that
// FormattedText renders as React elements.

import { linkify } from './linkify';
import { splitMentions } from './mentions';

const BULLET_RE = /^\s*[-*•]\s+(.*)$/;
const ORDERED_RE = /^\s*(\d{1,3})[.)]\s+(.*)$/;

// Exported for PostEditor's list continuation — one definition of "a list line".
export const matchListLine = (line) => {
  let m = line.match(BULLET_RE);
  if (m) return { kind: 'ul', content: m[1] };
  m = line.match(ORDERED_RE);
  if (m) return { kind: 'ol', number: Number(m[1]), content: m[2] };
  return null;
};

// ── Inline: bold / italic / links ─────────────────────────────

// Latin letters (incl. accented) and digits. Not \p{L}: Babel expands Unicode
// property escapes into multi-kilobyte character tables in the bundle.
const isWordChar = (ch) => !!ch && /[A-Za-z0-9\u00C0-\u024F]/.test(ch);
const isSpace = (ch) => !ch || /\s/.test(ch);

// Link ranges are found first and are off-limits to emphasis markers, so an
// underscore inside a URL (…/some_page_name) never starts or ends italics.
const linkRanges = (text) => {
  const ranges = [];
  let pos = 0;
  for (const seg of linkify(text)) {
    if (seg.type === 'link') ranges.push([pos, pos + seg.value.length]);
    pos += seg.value.length;
  }
  return ranges;
};

// A marker opens only when it is not glued to a preceding letter/number and is
// followed by a non-space; it closes only when preceded by a non-space and not
// followed by a letter/number. That keeps snake_case, 5 * 3 and a stray
// underscore literal.
const findEmphasis = (text, start, marker, ranges) => {
  const inLink = (i) => ranges.some(([a, b]) => i >= a && i < b);
  const len = marker.length;
  if (inLink(start)) return -1;
  if (isWordChar(text[start - 1])) return -1;
  if (isSpace(text[start + len])) return -1;
  if (marker === '_' && text[start + 1] === '_') return -1;
  for (let j = start + len + 1; j <= text.length - len; j++) {
    if (text[j] === '\n') return -1;             // emphasis never spans lines
    if (!text.startsWith(marker, j) || inLink(j)) continue;
    if (marker === '_' && (text[j + 1] === '_' || text[j - 1] === '_')) continue;
    if (isSpace(text[j - 1])) continue;
    if (isWordChar(text[j + len])) continue;
    return j;
  }
  return -1;
};

const plainNodes = (text) => linkify(text).flatMap(seg => (
  seg.type === 'link'
    ? [{ type: 'link', value: seg.value, href: seg.href }]
    : splitMentions(seg.value)
));

export const parseInline = (text) => {
  if (!text) return [];
  const ranges = linkRanges(text);
  const nodes = [];
  let plainStart = 0;
  let i = 0;
  while (i < text.length) {
    const marker = text.startsWith('**', i) ? '**' : text[i] === '_' ? '_' : null;
    const close = marker ? findEmphasis(text, i, marker, ranges) : -1;
    if (close === -1) { i += 1; continue; }
    if (i > plainStart) nodes.push(...plainNodes(text.slice(plainStart, i)));
    nodes.push({
      type: marker === '**' ? 'strong' : 'em',
      children: parseInline(text.slice(i + marker.length, close)),
    });
    i = close + marker.length;
    plainStart = i;
  }
  if (plainStart < text.length) nodes.push(...plainNodes(text.slice(plainStart)));
  return nodes;
};

// ── Blocks: paragraphs and lists ──────────────────────────────

// Consecutive non-list lines stay together as ONE paragraph block, blank lines
// included, rendered with whitespace-pre-wrap — so a post written before
// formatting existed renders exactly as it did. Blank lines only get trimmed
// where they border a list, which has its own spacing.
export const parseFormatted = (text) => {
  if (!text) return [];
  const blocks = [];
  let para = [];

  const flushPara = () => {
    while (para.length && !para[0].trim()) para.shift();
    while (para.length && !para[para.length - 1].trim()) para.pop();
    if (para.length) blocks.push({ type: 'p', children: parseInline(para.join('\n')) });
    para = [];
  };

  for (const line of text.split('\n')) {
    const item = matchListLine(line);
    if (!item) { para.push(line); continue; }
    flushPara();
    const last = blocks[blocks.length - 1];
    const node = { children: parseInline(item.content) };
    if (last && last.type === item.kind) {
      last.items.push(node);
    } else {
      blocks.push(item.kind === 'ol'
        ? { type: 'ol', start: item.number, items: [node] }
        : { type: 'ul', items: [node] });
    }
  }
  flushPara();
  return blocks;
};

// ── Editor highlighting ───────────────────────────────────────

// For PostEditor's styled layer behind the textarea. Unlike parseFormatted this
// keeps EVERY character, markers included, flagged so they can be dimmed.
// ⚠️ The segments must join back to exactly `text` — the layer only lines up
// with the caret if it contains the same characters in the same order.
// Emphasis rules are the same findEmphasis calls, so what highlights in the
// editor is what renders in the post.
const highlightInline = (text, style, out) => {
  if (!text) return;
  const ranges = linkRanges(text);
  const pushPlain = (slice) => {
    for (const seg of linkify(slice)) {
      if (seg.type === 'link') { out.push({ text: seg.value, ...style, link: true }); continue; }
      for (const part of splitMentions(seg.value)) {
        out.push({ text: part.value, ...style, mention: part.type === 'mention' });
      }
    }
  };
  let plainStart = 0;
  let i = 0;
  while (i < text.length) {
    const marker = text.startsWith('**', i) ? '**' : text[i] === '_' ? '_' : null;
    const close = marker ? findEmphasis(text, i, marker, ranges) : -1;
    if (close === -1) { i += 1; continue; }
    if (i > plainStart) pushPlain(text.slice(plainStart, i));
    const inner = { ...style, [marker === '**' ? 'bold' : 'italic']: true };
    out.push({ text: marker, ...inner, marker: true });
    highlightInline(text.slice(i + marker.length, close), inner, out);
    out.push({ text: marker, ...inner, marker: true });
    i = close + marker.length;
    plainStart = i;
  }
  if (plainStart < text.length) pushPlain(text.slice(plainStart));
};

const LIST_PREFIX_RE = /^\s*(?:[-*•]|\d{1,3}[.)])\s+/;

export const highlightSegments = (text) => {
  const out = [];
  (text ?? '').split('\n').forEach((line, idx) => {
    if (idx > 0) out.push({ text: '\n' });
    const prefix = matchListLine(line) ? line.match(LIST_PREFIX_RE)[0] : '';
    if (prefix) out.push({ text: prefix, marker: true });
    highlightInline(line.slice(prefix.length), {}, out);
  });
  return out;
};

// ── Previews ──────────────────────────────────────────────────

// For surfaces that clamp or cut a post (table list, feed "Read more"): bold,
// italic and links survive, but lists flatten to "• item" lines inside ONE
// inline run. line-clamp only behaves on a single block, and a cut through a
// real <ul> has nowhere sensible to put the ellipsis.
//
// Cutting happens on the parsed tree, not the raw string, so it can never
// leave a dangling "**" — which is what the previous strip-then-cut approach
// was protecting against at the cost of dropping formatting entirely.
export const flattenFormatted = (text) => {
  const out = [];
  parseFormatted(text).forEach((block, b) => {
    if (b > 0) out.push({ type: 'text', value: '\n' });
    if (block.type === 'p') { out.push(...block.children); return; }
    block.items.forEach((item, i) => {
      if (i > 0) out.push({ type: 'text', value: '\n' });
      out.push({ type: 'text', value: block.type === 'ol' ? `${block.start + i}. ` : '• ' });
      out.push(...item.children);
    });
  });
  return out;
};

// Keeps about `max` visible characters. A link or mention is never cut (a
// clipped URL or @handle would still be a live link to the wrong place) — one
// that straddles the cut is kept whole.
export const truncateNodes = (nodes, max) => {
  let remaining = max;
  let truncated = false;
  const walk = (list) => {
    const kept = [];
    for (const node of list) {
      if (remaining <= 0) { truncated = true; break; }
      if (node.children) {
        const children = walk(node.children);
        if (children.length) kept.push({ ...node, children });
      } else if (node.type === 'link' || node.type === 'mention' || node.value.length <= remaining) {
        kept.push(node);
        remaining -= node.value.length;
      } else {
        kept.push({ ...node, value: node.value.slice(0, remaining) });
        remaining = 0;
        truncated = true;
      }
    }
    return kept;
  };
  const kept = walk(nodes);
  return { nodes: kept, truncated };
};

export const formattedPreview = (text, max) => {
  const nodes = flattenFormatted(text);
  return max ? truncateNodes(nodes, max) : { nodes, truncated: false };
};
